import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatContactName } from "../lib/contactLabel";
import type {
  AgendaTaakGroup,
  ContactByTaak,
  RosterPlanningRow,
  RosterPlanningSlot,
} from "@domaddress/shared";

type TaakRow = { id: number; name: string };

type FilterValue = "" | `taak:${string}` | `group:${number}`;

function formatOccurrenceDate(isoDate: string, startDatetime: string): string {
  const d = isoDate || startDatetime.slice(0, 10);
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTimeRange(start: string, end: string): string {
  const t = (s: string) => s.split(" ")[1]?.slice(0, 5) ?? "";
  const a = t(start);
  const b = t(end);
  if (!a) return "";
  return b && b !== a ? `${a} – ${b}` : a;
}

export default function RostersPage() {
  const [taken, setTaken] = useState<TaakRow[]>([]);
  const [taakGroups, setTaakGroups] = useState<AgendaTaakGroup[]>([]);
  const [filter, setFilter] = useState<FilterValue>("");
  const [range, setRange] = useState<"3m" | "6m">("3m");
  const [rows, setRows] = useState<RosterPlanningRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contactsCache, setContactsCache] = useState<Record<string, ContactByTaak[]>>({});
  const [assigningKey, setAssigningKey] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    const [tRes, gRes] = await Promise.all([
      api.get<{ data: TaakRow[] }>("/api/meta/taken"),
      api.get<{ data: AgendaTaakGroup[] }>("/api/taak-groups"),
    ]);
    setTaken(tRes.data.data);
    setTaakGroups(gRes.data.data);
  }, []);

  const loadPlanning = useCallback(async () => {
    if (!filter) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ range });
      if (filter.startsWith("group:")) {
        params.set("group_id", filter.slice(6));
      } else if (filter.startsWith("taak:")) {
        params.set("taak", filter.slice(5));
      }
      const res = await api.get<{ data: RosterPlanningRow[] }>(`/api/rosters/planning?${params}`);
      setRows(res.data.data);
    } catch {
      setError("Planning laden mislukt.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, range]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    void loadPlanning();
  }, [loadPlanning]);

  const slotKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      for (const slot of row.slots) {
        keys.add(`${slot.werkgroep_group_id}:${slot.role_name}`);
      }
    }
    return [...keys];
  }, [rows]);

  useEffect(() => {
    if (slotKeys.length === 0) return;
    let cancelled = false;
    async function prefetch() {
      const next: Record<string, ContactByTaak[]> = { ...contactsCache };
      for (const key of slotKeys) {
        if (next[key]) continue;
        const [gid, taak] = key.split(":");
        const params = new URLSearchParams({
          taak: taak ?? "",
          group_id: gid ?? "",
        });
        try {
          const res = await api.get<{ data: ContactByTaak[] }>(
            `/api/meta/contacts-by-taak?${params}`
          );
          if (!cancelled) next[key] = res.data.data;
        } catch {
          if (!cancelled) next[key] = [];
        }
      }
      if (!cancelled) setContactsCache(next);
    }
    void prefetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cache merge per fetch
  }, [slotKeys.join("|")]);

  async function assignContact(
    row: RosterPlanningRow,
    slot: RosterPlanningSlot,
    contactId: number | ""
  ) {
    const key = `${row.event_id}-${row.occurrence_date}-${slot.role_name}-${slot.slot_index}`;
    setAssigningKey(key);
    try {
      let rosterId = slot.roster_id;
      if (!rosterId) {
        const created = await api.post<{ data: { id: number } }>("/api/rosters", {
          event_id: row.event_id,
          roster_date: row.occurrence_date,
          werkgroep_group_id: slot.werkgroep_group_id,
          role_name: slot.role_name,
        });
        rosterId = created.data.data.id;
      }
      if (contactId === "") {
        if (slot.assignment?.id) {
          await api.delete(`/api/rosters/${rosterId}/assignments/${slot.assignment.id}`);
        }
      } else {
        await api.post(`/api/rosters/${rosterId}/assignments`, { contact_id: contactId });
      }
      await loadPlanning();
    } finally {
      setAssigningKey(null);
    }
  }

  const isGroupFilter = filter.startsWith("group:");

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Roosters</h1>
          <p className="page-subtitle">
            Kies een taak of taakgroep en plan mensen in op alle komende momenten waar die taak
            nodig is.
          </p>
        </div>
      </header>

      <div className="card roster-planning-filters">
        <div className="form-row">
          <label htmlFor="roster-filter">Taak of groep</label>
          <select
            id="roster-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterValue)}
          >
            <option value="">Kies een taak of groep…</option>
            {taakGroups.length > 0 && (
              <optgroup label="Taakgroepen">
                {taakGroups.map((g) => (
                  <option key={`g-${g.id}`} value={`group:${g.id}`}>
                    {g.name} ({g.members.join(", ")})
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Losse taken">
              {taken.map((t) => (
                <option key={`t-${t.id}`} value={`taak:${t.name}`}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="roster-range">Periode</label>
          <select
            id="roster-range"
            value={range}
            onChange={(e) => setRange(e.target.value as "3m" | "6m")}
          >
            <option value="3m">Komende 3 maanden</option>
            <option value="6m">Komende 6 maanden</option>
          </select>
        </div>
        {isGroupFilter && (
          <p className="roster-planning-hint">
            Bij een groep staan de gekoppelde taken naast elkaar op één regel per datum.
          </p>
        )}
      </div>

      {!filter && (
        <p className="roster-planning-empty">
          Selecteer hierboven een taak of groep om de planning te zien. Taakgroepen maak je aan
          onder <Link to="/rooster-sjablonen">Rooster-sjablonen</Link>.
        </p>
      )}

      {filter && loading && <p className="roster-planner-loading">Planning laden…</p>}
      {error && <p className="form-error">{error}</p>}

      {filter && !loading && rows.length === 0 && (
        <p className="roster-planning-empty">
          Geen geplande momenten in deze periode waar deze taak nodig is.
        </p>
      )}

      {filter && !loading && rows.length > 0 && (
        <div className="card roster-planning-table-wrap">
          <table className="roster-planning-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Evenement</th>
                <th>{isGroupFilter ? "Taken &amp; inplanning" : "Inplanning"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.event_id}-${row.occurrence_date}-${row.slots.map((s) => s.role_name).join()}`}>
                  <td className="roster-planning-date">
                    <strong>{formatOccurrenceDate(row.occurrence_date, row.start_datetime)}</strong>
                    <span className="roster-planning-time">
                      {formatTimeRange(row.start_datetime, row.end_datetime)}
                    </span>
                  </td>
                  <td className="roster-planning-event">
                    <Link to={`/evenementen/${row.event_id}`}>{row.event_title}</Link>
                  </td>
                  <td>
                    <div
                      className={`roster-planning-slots${isGroupFilter ? " roster-planning-slots--group" : ""}`}
                    >
                      {row.slots.map((slot) => {
                        const cacheKey = `${slot.werkgroep_group_id}:${slot.role_name}`;
                        const candidates = contactsCache[cacheKey] ?? [];
                        const assignKey = `${row.event_id}-${row.occurrence_date}-${slot.role_name}-${slot.slot_index}`;
                        const busy = assigningKey === assignKey;
                        return (
                          <div key={assignKey} className="roster-planning-slot">
                            {isGroupFilter && (
                              <span className="roster-planning-slot-label">{slot.role_name}</span>
                            )}
                            <select
                              className="roster-planning-contact-select"
                              disabled={busy}
                              value={slot.assignment?.contact_id ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                void assignContact(
                                  row,
                                  slot,
                                  v === "" ? "" : Number(v)
                                );
                              }}
                            >
                              <option value="">— Nog niet ingepland —</option>
                              {slot.assignment && (
                                <option value={slot.assignment.contact_id}>
                                  {formatContactName(slot.assignment)}
                                </option>
                              )}
                              {candidates
                                .filter((c) => c.id !== slot.assignment?.contact_id)
                                .map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {formatContactName(c)}
                                  </option>
                                ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
